import { useAuth } from './useAuth'
import { useDocument } from './useFirestore'
import type { UserSubscription, UserUsage, SubscriptionPlan } from '@/types'
import { PLANS, PLAN_LIMITS } from '@/config/plans'

function getMonthKey() {
  return new Date().toISOString().slice(0, 7) // YYYY-MM
}

/** Resolve the active plan tier, validating the subscription period */
function resolveActivePlan(subscription: UserSubscription | null): SubscriptionPlan {
  if (!subscription) return 'free'
  const { plan, currentPeriodEnd } = subscription
  if (plan === 'free' || !currentPeriodEnd) return 'free'
  if (currentPeriodEnd.toMillis() <= Date.now()) return 'free'
  if (plan === 'lite' || plan === 'pro') return plan
  return 'free'
}

export interface SubscriptionState {
  /** The resolved active plan id */
  plan: SubscriptionPlan
  /** True only for active Pro subscribers */
  isPro: boolean
  /** True only for active Lite subscribers */
  isLite: boolean
  /** True for Lite OR Pro — use this to gate features available from Lite upward */
  isAtLeastLite: boolean
  /** Feature flags resolved for the active plan — check these instead of plan names */
  features: typeof PLANS.free.features
  /** Usage limits for the active plan */
  limits: typeof PLAN_LIMITS.free | typeof PLAN_LIMITS.lite | typeof PLAN_LIMITS.pro
  subscription: (UserSubscription & { id: string }) | null
  usage: (UserUsage & { id: string }) | null
  loading: boolean
  /** How many responses remain this month */
  responsesRemaining: number | null
  /** How many AI prompts remain this month */
  aiPromptsRemaining: number
}

export function useSubscription(): SubscriptionState {
  const { user } = useAuth()
  const isSignedIn = !!user && !user.isAnonymous

  const { data: subscription, loading: subLoading } = useDocument<UserSubscription>(
    isSignedIn ? `users/${user!.uid}/subscription/current` : null,
  )

  const { data: usage, loading: usageLoading } = useDocument<UserUsage>(
    isSignedIn ? `users/${user!.uid}/usage/${getMonthKey()}` : null,
  )

  const plan = resolveActivePlan(subscription ?? null)
  const isPro = plan === 'pro'
  const isLite = plan === 'lite'
  const isAtLeastLite = isPro || isLite

  const planConfig = PLANS[plan]
  const features = planConfig.features
  const limits = planConfig.limits

  const responsesUsed = usage?.responsesCount ?? 0
  const aiUsed = usage?.aiPromptsUsed ?? 0

  const responsesRemaining = limits.responsesPerMonth === Infinity
    ? null
    : Math.max(0, limits.responsesPerMonth - responsesUsed)

  const aiPromptsRemaining = Math.max(0, limits.aiPromptsPerMonth - aiUsed)

  return {
    plan,
    isPro,
    isLite,
    isAtLeastLite,
    features,
    limits,
    subscription: subscription ?? null,
    usage: usage ?? null,
    loading: subLoading || usageLoading,
    responsesRemaining,
    aiPromptsRemaining,
  }
}
